import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn, trackByValue } from '@y42/angular/util';
import { ColumnDataType, ColumnTypesStateService } from '@y42/column-type';
import { DataTestType } from '@y42/data-test';
import { ColumnTypeEnum } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { DataTestBuilderFormService } from '../../data-test-builder-form.service';

@UntilDestroy()
@Component({
  selector: 'y42-schema-test',
  templateUrl: './schema-test.component.html',
  styleUrls: ['./schema-test.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchemaTestComponent {
  constructor(
    private readonly formService: DataTestBuilderFormService,
    private readonly columnTypesStateService: ColumnTypesStateService,
  ) {}

  private readonly columnTypes$ = this.columnTypesStateService.select(
    (x) => x.entityState?.filter((col) => isNotNullish(col.columnType)) ?? [],
  );

  readonly schemaForm = this.formService.getFormByTestType(DataTestType.Schema);
  readonly trackByFn = trackByValue;
  columnTypesTrackByFn = getTrackByFn<ColumnDataType>('name');
  readonly columnTypeEnumToName: { [k in ColumnTypeEnum]: string } = {
    [ColumnTypeEnum.String]: this.getColumnDataTypeName(ColumnTypeEnum.String),
    [ColumnTypeEnum.Boolean]: this.getColumnDataTypeName(ColumnTypeEnum.Boolean),
    [ColumnTypeEnum.Numeric]: this.getColumnDataTypeName(ColumnTypeEnum.Numeric),
    [ColumnTypeEnum.Float]: this.getColumnDataTypeName(ColumnTypeEnum.Float),
    [ColumnTypeEnum.Integer]: this.getColumnDataTypeName(ColumnTypeEnum.Integer),
    [ColumnTypeEnum.Dynamic]: this.getColumnDataTypeName(ColumnTypeEnum.Dynamic),
    [ColumnTypeEnum.Bytes]: this.getColumnDataTypeName(ColumnTypeEnum.Bytes),
    [ColumnTypeEnum.Datetime]: this.getColumnDataTypeName(ColumnTypeEnum.Datetime),
    [ColumnTypeEnum.Date]: this.getColumnDataTypeName(ColumnTypeEnum.Date),
    [ColumnTypeEnum.Time]: this.getColumnDataTypeName(ColumnTypeEnum.Time),
    [ColumnTypeEnum.Timestamp]: this.getColumnDataTypeName(ColumnTypeEnum.Timestamp),
    [ColumnTypeEnum.Array]: this.getColumnDataTypeName(ColumnTypeEnum.Array),
    [ColumnTypeEnum.Geo]: this.getColumnDataTypeName(ColumnTypeEnum.Geo),
    [ColumnTypeEnum.Struct]: this.getColumnDataTypeName(ColumnTypeEnum.Struct),
    [ColumnTypeEnum.ArrayString]: this.getColumnDataTypeName(ColumnTypeEnum.ArrayString),
  };

  readonly state = connectState(this, {
    columnTypes: this.columnTypes$,
  });

  async addSchemaRow() {
    await this.formService.addRowToSchemaTest();
  }

  async deleteSchemaRow(index: number) {
    await this.formService.removeRowFromSchemaTest(index);
  }

  private getColumnDataTypeName(columnType: ColumnTypeEnum): ColumnDataType['name'] {
    return this.state?.columnTypes?.find((x) => x.columnType === columnType)?.name ?? columnType;
  }
}
